import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageType } from 'react-images-uploading';
// @ts-ignore
import { CKEditor } from '@ckeditor/ckeditor5-react';
// @ts-ignore
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  InputText,
  InputTextType
} from '../../../../shared/components/core-ui/input-text/input-text.component';
import ImageUploader from '../../../../shared/components/core-ui/image-uploader/image-uploader.component';
import Button, { ButtonSize } from '../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../shared/hooks/window-resizer/window-resizer.hook';
import { FormElementData } from '../../../../shared/interfaces/form-element-data.interface';
import { BlogUiModel } from '../../../../shared/ui-models/blog.ui-model';
import { BLOG_IMAGE_ASPECT_RATIO } from '../../../shared/config/blog-module.config';

export interface EditBlogContentProps {
  data: {
    blog: BlogUiModel | undefined;
  };
  config: {
    isLoading: boolean;
    isNew: boolean;
  };
  eventHandlers: {
    submitHandler: (data: Partial<BlogUiModel>) => void;
    valuesChangeHandler: (data: Partial<BlogUiModel>) => void;
    imageUploadHandler: (file: ImageType) => void;
  };
}

/**
 * functional component EditBlogContent — the English side of a blog post
 */
export const EditBlogContent: React.FC<EditBlogContentProps> = ({
  data,
  config,
  eventHandlers
}) => {
  const { t } = useTranslation();
  const isMobileView = useWindowResizer();

  const [newHeader, setNewHeader] = useState<FormElementData<string | undefined>>({
    value: undefined,
    isValid: false,
    changed: false
  });
  const [newBody, setNewBody] = useState<FormElementData<string | undefined>>({
    value: undefined,
    isValid: false,
    changed: false
  });

  useEffect(() => {
    setNewHeader({ value: data.blog?.header || '', isValid: false, changed: false });
    setNewBody({ value: data.blog?.body || '', isValid: false, changed: false });
  }, [data.blog]);

  const handleSubmit = () => {
    const updatedData: Partial<BlogUiModel> = {};

    if (newHeader.changed) {
      updatedData.header = newHeader.value;
    }
    if (newBody.changed) {
      updatedData.body = newBody.value;
    }

    eventHandlers.submitHandler(updatedData);
  };

  const isSubmitDisabled = config.isLoading || (!newHeader.changed && !newBody.changed);

  return (
    <div className="flex w-full flex-col gap-[1.5rem]">
      <div className="text-[1.8rem] font-bold text-primary-500">
        {t('BLOG_MODULE.EDIT_CONTAINER.EN.TITLE')}
      </div>

      <div className="flex w-full flex-col gap-[0.5rem]">
        <div className="font-medium">{t('BLOG_MODULE.EDIT_CONTAINER.EN.HEADER')}</div>
        <InputText
          config={{
            autoFocus: false,
            type: InputTextType.text,
            required: true,
            disabled: config.isLoading
          }}
          data={{
            placeholder: t('BLOG_MODULE.EDIT_CONTAINER.EN.HEADER_PLACEHOLDER'),
            errorMessage: t('BLOG_MODULE.EDIT_CONTAINER.EN.HEADER_ERROR'),
            value: newHeader.value || ''
          }}
          eventHandlers={{
            onChangeHandler: (value, isValid) => {
              setNewHeader({ value, isValid, changed: true });
              eventHandlers.valuesChangeHandler({ header: value });
            }
          }}
        />
      </div>

      <div className="flex w-full flex-col gap-[0.5rem]">
        <div className="font-medium">{t('BLOG_MODULE.EDIT_CONTAINER.EN.IMAGE')}</div>
        <ImageUploader
          data={{ imageUrl: data.blog?.image }}
          config={{ enableCrop: true, cropAspectRatio: BLOG_IMAGE_ASPECT_RATIO }}
          eventHandlers={{
            fileUploadHandler: (file: ImageType) => {
              eventHandlers.imageUploadHandler(file);
            },
            imageRemovedHandler: () => {
              return;
            }
          }}
        />
      </div>

      {newBody.value !== undefined && (
        <div className="flex w-full flex-col gap-[0.5rem]">
          <div className="font-medium">{t('BLOG_MODULE.EDIT_CONTAINER.EN.BODY')}</div>
          <CKEditor
            editor={ClassicEditor}
            data={newBody.value}
            onChange={(_event: any, editor: any) => {
              const editorData: string = editor.getData();
              setNewBody({ value: editorData, isValid: true, changed: true });
              eventHandlers.valuesChangeHandler({ body: editorData });
            }}
          />
        </div>
      )}

      <div className="flex w-full justify-end">
        <Button
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          {config.isNew
            ? t('BLOG_MODULE.EDIT_CONTAINER.CREATE')
            : t('BLOG_MODULE.EDIT_CONTAINER.SAVE')}
        </Button>
      </div>
    </div>
  );
};

export default EditBlogContent;
